import { useEffect, useRef, useState } from 'react';
import { supabase } from './supabase';

// ── SUPABASE-BACKED PERSISTENCE ──────────────────────────────────────────────
// Estado local-first: localStorage da lectura instantánea y respaldo offline,
// mientras que Supabase guarda una copia remota en una tabla key-value
// `app_state(key text primary key, value jsonb, updated_at timestamptz)`.
// Si Supabase no está disponible (sin tabla / sin red), la app sigue
// funcionando sólo con localStorage — toda la sincronización es tolerante a
// fallos y nunca lanza errores hacia la UI.

const TABLE = 'app_state';

// Evita reintentos ruidosos si la tabla remota no existe / no es accesible.
let remoteDisabled = false;
async function remoteLoad<T>(key: string): Promise<T | undefined> {
  if (remoteDisabled) return undefined;
  try {
    const {
      data,
      error
    } = await supabase.from(TABLE).select('value').eq('key', key).maybeSingle();
    if (error) {
      // 42P01 = tabla inexistente; desactivamos remoto para no insistir.
      if ((error as {
        code?: string;
      }).code === '42P01') remoteDisabled = true;
      return undefined;
    }
    return data?.value as T ?? undefined;
  } catch {
    return undefined;
  }
}
async function remoteSave<T>(key: string, value: T): Promise<void> {
  if (remoteDisabled) return;
  try {
    const {
      error
    } = await supabase.from(TABLE).upsert({
      key,
      value,
      updated_at: new Date().toISOString()
    }, {
      onConflict: 'key'
    });
    if (error && (error as {
      code?: string;
    }).code === '42P01') {
      remoteDisabled = true;
    }
  } catch {

    /* offline u otro fallo: ignoramos, localStorage ya tiene la copia */}
}
export function useStoredState<T>(key: string, initial: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === 'undefined') return initial;
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) as T : initial;
    } catch {
      return initial;
    }
  });

  // Hidratar desde Supabase una sola vez al montar (sin pisar ediciones locales).
  const hydrated = useRef(false);
  useEffect(() => {
    let cancelled = false;
    remoteLoad<T>(key).then(remote => {
      if (cancelled || remote === undefined) {
        hydrated.current = true;
        return;
      }
      // Sólo adoptamos el valor remoto si el usuario aún no ha tocado nada.
      if (!hydrated.current) {
        setValue(remote);
        try {
          window.localStorage.setItem(key, JSON.stringify(remote));
        } catch {

          /* ignore */}
      }
      hydrated.current = true;
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  // Persistir cambios en localStorage + Supabase (con debounce).
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {

      /* ignore quota errors */}
    // No empujamos a remoto hasta haber intentado hidratar (evita carrera).
    if (!hydrated.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      remoteSave(key, value);
    }, 400);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [key, value]);
  return [value, setValue];
}
export function csvCell(value: unknown): string {
  const str = String(value ?? '');
  if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
  return str;
}
export function downloadText(filename: string, contents: string, mime: string) {
  if (typeof window === 'undefined') return;
  const blob = new Blob([contents], {
    type: mime
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}